import { Injectable, inject } from '@angular/core';
import { CacheHttpService } from '@data-api/services/cache-http.service';
import { DataRetrieverHttpService } from '@data-api/services/data-retriever-http.service';
import { FilterCaching } from '@shared/models';
import { Dimension } from '@shared/models/dimension.model';
import { FilterValues } from '@shared/models/filter-caching.model';
import {
  GlobalFilter,
  GlobalFilterGroup,
  GlobalFiltersFlat,
} from '@shared/models/global-filter.model';
import { Filters } from '@shared/models/state/selected.model';
import { SubjectArea } from '@shared/models/state/subject-area.model';
import { DimensionService } from '@shared/services/dimension.service';
import { FiltersService } from '@shared/services/filters.service';
import { ToastrService } from '@shared/services/toaster.service';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

@Injectable()
export class GlobalFilterService {
  private readonly filtersService = inject(FiltersService);
  private readonly cacheHttpService = inject(CacheHttpService);
  private readonly dataRetrieverHttpService = inject(DataRetrieverHttpService);
  private readonly dimensionService = inject(DimensionService);
  private readonly toastr = inject(ToastrService);

  private someFiltersNotLoaded: boolean = false;
  public subjectAreaWithNoFilters: boolean = false;

  globalFiltersGroup: GlobalFilterGroup[];
  allDimensions: { [id: string]: Dimension } = {};
  globalFilters: GlobalFilter[];

  private subjectAreaFilters = new BehaviorSubject<number[]>([]);
  private globalFiltersFlat = new BehaviorSubject<GlobalFiltersFlat>({});
  private globalFiltersSelected = new BehaviorSubject<Filters>({});
  private globalFiltersCached = new BehaviorSubject<FilterCaching[]>([]);

  public globalFiltersFlat$ = this.globalFiltersFlat.asObservable();
  public globalFiltersSelected$ = this.globalFiltersSelected.asObservable();
  public globalFiltersCached$ = this.globalFiltersCached.asObservable();

  public setSubjectAreaFilters(subjectAreaFilters: number[]) {
    this.subjectAreaFilters.next(subjectAreaFilters);
  }

  public setGlobalFiltersFlat(globalFiltersFlat: GlobalFiltersFlat) {
    this.globalFiltersFlat.next(globalFiltersFlat);
  }

  public setGlobalFiltersSelected(globalFiltersSelected: Filters) {
    this.globalFiltersSelected.next(globalFiltersSelected);
  }

  public setGlobalFiltersCached(globalFiltersCached: FilterCaching[]) {
    this.globalFiltersCached.next(globalFiltersCached);
  }

  public setGlobalFilters(subjectArea: SubjectArea): Observable<{
    dimensions: { [id: string]: Dimension };
    filters: {
      globalFilters: GlobalFilterGroup[];
      filtersCached: FilterCaching[];
    };
  }> {
    this.someFiltersNotLoaded = false;
    this.subjectAreaWithNoFilters = false;
    const dimensionsRequest$ = this.dimensionService
      .getDimensions(subjectArea.dataSourceId)
      .pipe(
        catchError(err => {
          console.error(err);
          this.toastr.info(this.toastr.MESSAGE.fetchData.error.dimensions);
          return of(null);
        })
      );
    const allFiltersRequest$ = this.getAllFilters(subjectArea);
    return forkJoin({
      dimensions: dimensionsRequest$,
      filters: allFiltersRequest$,
    });
  }

  private getAllFilters(subjectArea: SubjectArea): Observable<{
    globalFilters: GlobalFilterGroup[];
    filtersCached: FilterCaching[];
  }> {
    return this.filtersService.getAllFilters().pipe(
      mergeMap((result: GlobalFilterGroup[]) => {
        if (result.length === 0) {
          this.subjectAreaWithNoFilters = true;
          return of({ globalFilters: [], filtersCached: [] });
        }

        const filtersIdsUsedInSubjectArea: Set<number> = new Set(
          subjectArea.subjectAreaFilters
        );

        const filtersIdsUsedInSubjectAreaDashboards: Set<number> = new Set(
          subjectArea.layoutFilters
        );

        const globalFiltersUsedInSubjectArea = result.flatMap(
          globalFilterGroup =>
            globalFilterGroup.globalFilters
              .filter(globalFilter =>
                filtersIdsUsedInSubjectArea.has(globalFilter.globalFilterId)
              )
              .map(globalFilter => ({
                ...globalFilter,
                isSubjectAreaFilter: true,
              }))
        );
        const globalFiltersUsedInLayout = result.flatMap(globalFilterGroup =>
          globalFilterGroup.globalFilters
            .filter(globalFilter =>
              filtersIdsUsedInSubjectAreaDashboards.has(
                globalFilter.globalFilterId
              )
            )
            .map(globalFilter => ({
              ...globalFilter,
              isSubjectAreaFilter: false,
            }))
        );

        const allGlobalFilters = [
          ...globalFiltersUsedInSubjectArea,
          ...globalFiltersUsedInLayout,
        ];
        const allDataSourceIds = [
          ...new Set(
            allGlobalFilters.map(globalFilter => globalFilter.dataSourceId)
          ),
        ];

        const shouldRefreshDataSources$ = allDataSourceIds.map(dataSourceId =>
          this.cacheHttpService
            .shouldRefresh(dataSourceId)
            .pipe(catchError(error => of(error)))
        );

        if (!allGlobalFilters.length) {
          this.subjectAreaWithNoFilters = true;
          return of({
            globalFilters: result,
            filtersCached: [],
          });
        }
        return forkJoin(shouldRefreshDataSources$).pipe(
          mergeMap(() => this.allGlobalFilterValues(allGlobalFilters, result))
        );
      })
    );
  }

  private allGlobalFilterValues(
    allGlobalFilters: GlobalFilter[],
    result: GlobalFilterGroup[]
  ): Observable<{
    globalFilters: GlobalFilterGroup[];
    filtersCached: FilterCaching[];
  }> {
    return forkJoin(
      allGlobalFilters.map(globalFilter => this.getValuesFilters(globalFilter))
    ).pipe(
      map(filtersCached => filtersCached.filter(filter => filter !== null)),
      map(filtersCached => ({
        globalFilters: result,
        filtersCached,
      }))
    );
  }

  private getValuesFilters({
    filterName,
    metricId: filterId,
    globalFilterId,
    isSubjectAreaFilter,
  }: GlobalFilter): Observable<{
    cachedFilterName: string;
    values: FilterValues[];
    defaultValues: Filters;
  }> {
    return this.dataRetrieverHttpService
      .getFilters({ filterId })
      .pipe(
        map(response => ({
          cachedFilterName: filterName,
          values: response?.response?.result,
          defaultValues: response?.response?.defaultValue,
          globalFilterId,
          isSubjectAreaFilter,
        }))
      )
      .pipe(
        catchError(() => {
          this.someFiltersNotLoaded = true;
          return of(null);
        })
      );
  }

  public handleFiltersResponse(filters: {
    globalFilters: GlobalFilterGroup[];
    filtersCached: FilterCaching[];
  }): void {
    const { globalFilters, filtersCached } = filters;
    this.globalFiltersGroup = globalFilters;
    this.setGlobalFiltersCached(filtersCached);

    if (this.subjectAreaWithNoFilters) {
      return;
    }

    if (filtersCached.length === 0) {
      this.toastr.info(this.toastr.MESSAGE.fetchData.error.filtersNotLoaded);
      return;
    }

    if (this.someFiltersNotLoaded) {
      this.toastr.info(
        this.toastr.MESSAGE.fetchData.error.someFiltersNotLoaded
      );
    }
  }

  public setDimensions(dimensions: { [id: string]: Dimension }): void {
    this.allDimensions = dimensions;
  }

  public transformToGlobalFiltersFlat(
    globalFilters: GlobalFilterGroup[],
    dimensions
  ) {
    globalFilters.forEach(globalFilter => {
      globalFilter.globalFilters.forEach((filter, index) => {
        filter.globalFilterDimensions.forEach(filterDimension => {
          if (dimensions[filterDimension.dimensionId]) {
            dimensions[filterDimension.dimensionId] = {
              ...dimensions[filterDimension.dimensionId],
              dimensionId: filterDimension.dimensionId,
              filterGroup: {
                globalFilterGroupId: globalFilter.globalFilterGroupId,
                groupName: globalFilter.groupName,
              },
              globalFilter: {
                globalFilterId: filter.globalFilterId,
                filterName: filter.filterName,
                level: index,
              },
              globalFilterDimensionId: filterDimension.globalFilterDimensionId,
              dataSourceId: filter.dataSourceId,
              endPointId: filter.metricId,
            };
          }
        });
      });
    });
    return dimensions;
  }
  public getFormattedGlobalFiltersFlat() {
    const filtersSelected$ = this.globalFiltersSelected$;

    filtersSelected$.subscribe((globalFiltersSelected: Filters) => {
      const dimensionsFlat = this.transformToGlobalFiltersFlat(
        this.globalFiltersGroup,
        this.allDimensions
      );
      const itemsSelected = Object.keys(globalFiltersSelected);
      const globalFiltersFlat = {};
      itemsSelected.forEach(select => {
        if (dimensionsFlat[select]) {
          dimensionsFlat[select].value = globalFiltersSelected[select];
          globalFiltersFlat[select] = {
            ...globalFiltersFlat,
            ...dimensionsFlat[select],
          };
        }
      });
      this.setGlobalFiltersFlat(globalFiltersFlat);
    });
  }

  public resetFilters(): Observable<Filters> {
    return this.globalFiltersCached$.pipe(
      map(defaultFilters =>
        defaultFilters.map(defaultFilter => defaultFilter.defaultValues)
      ),
      map(values => values.reduce((acc, curr) => ({ ...acc, ...curr }), {})),
      tap(defaultFilters => this.setGlobalFiltersSelected(defaultFilters))
    );
  }
}
